import { AbstractCollection } from '../../abstract/Collection';
import { AbstractDocument, Identifiable, Timestampable, Untrackable } from '../../abstract/Document';
import { type Wedding, Weddings } from '../Weddings';
import type { IGuest } from './Guests.types';
import { arrayRemove, arrayUnion, deleteField } from 'firebase/firestore';

@Identifiable
@Timestampable
@Untrackable
export class Guest extends AbstractDocument<IGuest> {
  readonly collections = {};
}

export class Guests extends AbstractCollection<Guest, IGuest> {
  static path = 'guests';

  constructor(document: Wedding) {
    super(document.collection(Guests.path), Guest);
  }

  get wedding() {
    return Weddings._.getById(this.reference.parent!.id);
  }

  /**
   * Removes a guest category with a specified ID.
   */
  removeCategory(id: string) {
    return this.wedding.set({
      guests: {
        categories: {
          data: {
            [id]: deleteField(),
          },
          sort: arrayRemove(id),
        },
      },
    });
  }

  /**
   * Removes a guest list table with a specified ID.
   */
  removeTable(id: string) {
    return this.wedding.set({
      guests: {
        tables: {
          data: {
            [id]: deleteField(),
          },
          sort: arrayRemove(id),
        },
      },
    });
  }

  /**
   * Resets `Wedding.guests` and drops all documents in the guests sub-collection.
   * If the wedding already has a previous `guests.estimate`, that will be persisted.
   */
  async reset() {
    const guests = Weddings.default.guests;
    const previous = await this.wedding.get();

    if (previous != null) {
      if (previous.guests?.estimate != null) {
        guests.estimate = previous.guests?.estimate;
      }
    }

    return await Promise.all([this.wedding.update({ guests }), this.delete(true)]);
  }

  /**
   * @deprecated Use `reset()` instead.
   */
  resetGuestList() {
    return this.reset();
  }

  /**
   * Creates or updates a guest category with a specified ID and name.
   * If the category doesn't yet exists, a new entry is created and the category is added at the end of the sort array.
   */
  upsertCategory(id: string, name: string) {
    return this.wedding.set({
      guests: {
        categories: {
          data: {
            [id]: name,
          },
          sort: arrayUnion(id),
        },
      },
    });
  }

  /**
   * Creates or updates a guest table with a specified ID and name.
   * If the table doesn't yet exists, a new entry is created and the table is added at the end of the sort array.
   */
  upsertTable(id: string, name: string) {
    return this.wedding.set({
      guests: {
        tables: {
          data: {
            [id]: name,
          },
          sort: arrayUnion(id),
        },
      },
    });
  }
}
